/**
 * 계약 정보 상세 컴포넌트
 * 계약의 기본 정보를 표시하는 컴포넌트입니다.
 * 계약명, 계약일시, 계약자명, 이용기간, 보증금, 이용료 등의 정보를 보여줍니다.
 */

import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import { ContractorNoti, DetailTotalData, disabledEsignEdit } from "src/api/contract/contract-types";
import { BaseButton, BaseRadio, BaseTooltip } from "src/components";
import { ViewYmdFormat, calculateDate, formatPhoneNumber, numberToStringWithComma } from "src/utils";
import BaseSection from "../../../../components/layout/BaseSection";
import ContractImageUpload from "../ContractImageUpload";
import EsignOnLink from "../editComponent/EsignOnLink";
import ContractRenewalModal from "../modal/ContractRenewalModal";
import ModifyAmountModalAccordion from "../modal/ModifyAmountModalAccordion";
import SnapShotModal from "../modal/SnapShotModal";
import ModuSignContractSection from "../moduSign/ModuSignContractSection";
import RelatedContractsSection from "./RelatedContractsSection";
import { useModal } from "src/recoil/modalState/hook";
import useEsignApi from "../../hooks/useEsignApi";
import { getPgcodeLabel } from "src/pages/product/product-types";

/**
 * 계약 기본 정보 props 인터페이스
 */
interface ContractBasicProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
  setData: any; // 데이터 업데이트 함수
  editable: boolean;
  contractSignType: { directContract: boolean; isEsign: boolean; isModuSign: boolean };
  onChangeContractSignType: (contractSignType: { directContract: boolean; isEsign: boolean; isModuSign: boolean }) => void;
}

/**
 * 계약 정보 상세 컴포넌트
 * @param detailData 계약 상세 정보
 * @param setData 데이터 업데이트 함수
 */
const ContractInfoDetail = ({ detailData, setData, editable, contractSignType, onChangeContractSignType }: ContractBasicProp) => {
  const R = "RENTAL"; // 임대 유형 상수
  const { contract, contractManage, scheduleList } = detailData;
  const { setBaseModal } = useModal();
  const { getPartnerEsignCheck } = useEsignApi();
  // 상태 관리
  const [textGrey, setTextGrey] = useState("contents-container__grid"); // 텍스트 색상 상태
  const [isSnapShotOpen, setIsSnapShotOpen] = useState(false); // 스냅샷 모달 상태
  const [document, setDocument] = useState<any[]>([]); // 계약 문서 상태
  const [require, setRequire] = useState<any[]>([]); // 필수 문서 상태
  const [rentalSchedule, setRentalSchedule] = useState<BillingDetail>(); // 임대 스케줄 상태
  const [isModifyAmountOpen, setIsModifyAmountOpen] = useState(false); // 금액 수정 모달 상태
  const [isRenewalOpen, setIsRenewalOpen] = useState(false); // 재계약 생성 모달 상태

  const [calDate, setCalDate] = useState(""); // 계산된 날짜 상태
  const [s2TaxNotiOne, setS2TaxNotiOne] = useState<any>({
    // 세금계산서 알림 상태
    email: "",
    isDeleted: false,
    contact: "",
    receiveNotiType: "TAX_INVOICE",
    description: "",
    errorTextEmail: "",
  });

  const handleCheckEsignAvailable = async (esignType: "ESIGN_ON" | "MODU_SIGN") => {
    const hasEsignPermission = await getPartnerEsignCheck();
    const { isESignonAvailable, isModuSignAvailable } = hasEsignPermission!;

    // 권한 없을 때 기본 상태로 돌리는 공통 함수
    const setDefaultContractState = () => {
      onChangeContractSignType({ directContract: true, isEsign: false, isModuSign: false });
    };

    // 권한 없을 때 모달 표시하는 공통 함수
    const showPermissionModal = () => {
      setBaseModal({
        title: "파트너 권한이 없습니다.",
        children: <p>전자 서명을 이용하시려면 관리자에게 문의해 주세요.</p>,
        isOpen: true,
        onClick: () => setBaseModal({ isOpen: false }),
        btnRightTitle: "확인",
      });
      setDefaultContractState();
    };

    switch (esignType) {
      case "ESIGN_ON":
        if (!isESignonAvailable) {
          showPermissionModal();
        } else {
          onChangeContractSignType({ directContract: false, isEsign: true, isModuSign: false });
        }
        break;

      case "MODU_SIGN":
        if (!isModuSignAvailable) {
          showPermissionModal();
        } else {
          onChangeContractSignType({ directContract: false, isEsign: false, isModuSign: true });
        }
        break;
    }
  };

  /**
   * 초기 데이터 설정
   * 계약 문서, 필수 문서, 임대 스케줄, 세금계산서 알림 등의 초기 데이터를 설정합니다.
   */
  const initDatas = useCallback(() => {
    if (detailData) {
      // 계약 문서와 필수 문서 분리
      const doc = contract?.mediaList!.filter((media: any) => media.serviceTag === "CONTRACT_DOCUMENT");
      let req = contract?.mediaList!.filter((media: any) => media.serviceTag !== "CONTRACT_DOCUMENT");
      req = _.sortBy(req, ["orderNums"]);

      if (doc) {
        setDocument(doc);
      }
      if (req) {
        setRequire(req);
      }

      // 임대 스케줄 설정
      const schedule = scheduleList?.find((sd: BillingDetail) => sd.supplyType === R);
      let returnText = calculateDate(contract?.contractStartTime!, contract?.contractEndTime!, contractManage?.spaceProductType!);
      if (returnText !== "") {
        setCalDate(returnText);
      }

      // 세금계산서 알림 설정
      const billContractorNoti = _.cloneDeep(contract?.billContactList);
      const tax = billContractorNoti!.filter((noti: ContractorNoti) => noti.receiveNotiType !== "TALK_OFFICIALDOC");

      if (detailData.product.productType === "TIME_COURT" && tax.length > 0) {
        setS2TaxNotiOne(tax[0]);
      }

      setRentalSchedule(schedule);
    }
  }, [
    contract?.contractEndTime,
    contract?.contractStartTime,
    contract?.eSignList,
    contract?.mediaList,
    contractManage?.spaceProductType,
    detailData,
    scheduleList,
  ]);

  // 초기 데이터 로드
  useEffect(() => {
    initDatas();
  }, [initDatas]);

  return (
    <>
      <div className="flex-center">
        <BaseSection
          titleChildren={
            <div className="flex-center">
              <p>계약명(필수)</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4 mt1"
                size={16}
                tooltip="계약명을 설정하면, 앱/알림톡/이메일 공문에 관리자가 입력한 정보가 출력됩니다."
              />
            </div>
          }
          children={
            <div className="flex-row flex-center-start w-100 font14">
              {contract?.contractName ? (
                <div className={contract.contractName.length > 15 ? "" : "mr5"}>
                  {contract.contractName.length > 25 ? (
                    <div className={"flex-center-start"}>
                      <p>{contract.contractName.substring(0, 15) + "..."}</p>
                      <BaseTooltip contents={""} tooltip={contract.contractName} touchIcon={"QUESTION"} />
                    </div>
                  ) : (
                    contract.contractName
                  )}
                </div>
              ) : (
                <p>-</p>
              )}
            </div>
          }
        />
        <BaseSection
          titleChildren={
            <div className="flex-center">
              <p>계약명(선택)</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4 mt1"
                size={16}
                tooltip="방문자 셀프 등록 서비스에서 계약명(필수)와 계약명(선택) 모두 검색할 수 있습니다."
              />
            </div>
          }
          children={
            <div className="flex-row flex-center-start w-100 font14">
              <BaseTooltip isSingleLine contents={contract?.contractNickname || "-"} />
            </div>
          }
        />
      </div>
      <div className="flex-center">
        <BaseSection
          title={"계약자명"}
          children={
            <div className="flex-row flex-center-start w-100 font14">
              <div className={detailData?.contract?.contractorName && detailData?.contract?.contractorName.length > 15 ? "" : "mr5"}>
                {contract && contract?.contractorName && contract?.contractorName!.length > 25 ? (
                  <div className={"flex-center-start"}>
                    <p>{contract?.contractorName!.substring(0, 15) + "..."}</p>
                    <BaseTooltip contents={""} tooltip={contract?.contractorName} touchIcon={"QUESTION"} />
                  </div>
                ) : (
                  contract?.contractorName
                )}{" "}
              </div>
            </div>
          }
        />
        <BaseSection
          title={"계약일시"}
          value={contract?.contractDate ? moment(contract?.contractDate).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}
        />
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" ? (
          <>
            <BaseSection
              title={"이용 시작일시"}
              value={contract?.contractStartTime ? moment(contract?.contractStartTime).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"}
            />
            <BaseSection
              title={"이용 종료일시"}
              children={
                <p>
                  {contract?.contractEndTime ? moment(contract?.contractEndTime).format(ViewYmdFormat.YYYY_MM_DD_HH_MM) : "-"} ({calDate ? calDate : "-"}
                  )
                </p>
              }
            />
          </>
        ) : (
          <>
            <BaseSection
              title={"계약 ID"}
              children={
                <div className="flex-row flex-center-start">
                  <span className="mr10">{contract?.contractId ? contract?.contractId : "-"}</span>
                  {textGrey !== "" && (
                    <BaseButton
                      title="계약 상품정보 스냅샷"
                      className="color-white"
                      disabled={
                        contract?.contractStep === "APPLY_RECEIVED" ||
                        contract?.contractStep === "APPLY_CANCEL" ||
                        contract?.contractStep === "APPLY_CONFIRM"
                      }
                      onClick={() => setIsSnapShotOpen(true)}
                    />
                  )}
                  <SnapShotModal
                    isOpen={isSnapShotOpen}
                    contractId={Number(contract?.contractId)}
                    detailData={detailData}
                    onClose={() => {
                      setIsSnapShotOpen(false);
                    }}
                  />
                </div>
              }
            />
            <BaseSection />
          </>
        )}
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection title={"보증금"} value={numberToStringWithComma(Number(contract?.earnestAmount) + Number(contract?.balanceAmount)) + " 원"} />
        )}
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection
            title={"이용료(부가세 별도)"}
            children={
              <div className="flex-row flex-center-start ">
                <span className="mr16">{contract?.payAmount ? numberToStringWithComma(contract?.payAmount) + " 원" : "-"}</span>
                <BaseButton
                  title="청구 금액 확인"
                  className="color-white"
                  disabled={
                    contract?.contractStep === "APPLY_RECEIVED" ||
                    contract?.contractStep === "APPLY_CANCEL" ||
                    contract?.contractStep === "APPLY_CONFIRM" ||
                    contract?.contractStep === "CONTRACT_PLAN"
                  }
                  onClick={() => setIsModifyAmountOpen(true)}
                />
                <ModifyAmountModalAccordion
                  detailData={detailData}
                  scheduleId={Number(rentalSchedule?.scheduleId)}
                  isOpen={isModifyAmountOpen}
                  onClose={() => {
                    setIsModifyAmountOpen(false);
                  }}
                  onClick={() => {}}
                  viewOnly={true}
                />
              </div>
            }
          />
        )}
      </div>

      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection
            title={"ㄴ 계약금"}
            children={
              <div className="flex-row flex-center-start">
                <div className={contract?.earnestPayStatus === "PAY_WAIT" ? `chip blue` : "chip green"}>
                  {contract?.earnestPayStatus === "PAY_WAIT" ? "결제 대기" : "결제 완료"}
                </div>
                <span className="ml5">{Number(contract?.earnestAmount) > 0 ? numberToStringWithComma(contract?.earnestAmount) + " 원" : "-"}</span>
              </div>
            }
          />
        )}
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection
            title={"납부 유형"}
            children={
              <div className="flex-row flex-center-start">
                <div className={contract?.costType !== "COST_BILLING_ONE_TIME" ? `chip blue` : "chip green"}>
                  {contract?.costType !== "COST_BILLING_ONE_TIME" ? "정기 납부" : "일시 납부"}
                </div>
              </div>
            }
          />
        )}
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection
            title={"ㄴ 잔금"}
            children={
              <div className="flex-row flex-center-start">
                <div className={contract?.balancePayStatus === "PAY_WAIT" ? `chip blue` : "chip green"}>
                  {contract?.balancePayStatus === "PAY_WAIT" ? "결제 대기" : "결제 완료"}
                </div>
                <span className="ml5">{Number(contract?.balanceAmount) > 0 ? numberToStringWithComma(contract?.balanceAmount) + " 원" : "-"}</span>
              </div>
            }
          />
        )}
      </div>
      <div className="flex-center">
        {detailData?.product?.productType !== "TIME_COURT" ? (
          <BaseSection
            title={"계약 ID"}
            children={
              <div className="flex-row flex-center-start">
                <span className="mr10">{contract?.contractId ? contract?.contractId : "-"}</span>
                {textGrey !== "" && (
                  <BaseButton
                    title="계약 상품정보 스냅샷"
                    className="color-white"
                    disabled={
                      contract?.contractStep === "APPLY_RECEIVED" ||
                      contract?.contractStep === "APPLY_CANCEL" ||
                      contract?.contractStep === "APPLY_CONFIRM"
                    }
                    onClick={() => setIsSnapShotOpen(true)}
                  />
                )}
                <SnapShotModal
                  isOpen={isSnapShotOpen}
                  contractId={Number(contract?.contractId)}
                  detailData={detailData}
                  onClose={() => {
                    setIsSnapShotOpen(false);
                  }}
                />
              </div>
            }
          />
        ) : (
          <BaseSection title={"세금계산서 수신 이메일"} children={<p>{s2TaxNotiOne.email}</p>} />
        )}
        <BaseSection
          title={"계약 형태"}
          children={
            <div className="flex-row flex-center-start">
              <BaseRadio
                id="contractType1"
                name="contractType"
                label="직접 계약"
                checked={contractSignType.directContract}
                onChange={() => {
                  onChangeContractSignType({ directContract: true, isEsign: false, isModuSign: false });
                }}
                disabled={disabledEsignEdit.includes(contract?.contractStep!)}
              />
              <BaseRadio
                id="contractType2"
                name="contractType"
                className="mx10"
                label="이싸인온"
                checked={contractSignType.isEsign}
                onChange={() => {
                  handleCheckEsignAvailable("ESIGN_ON");
                }}
                disabled={disabledEsignEdit.includes(contract?.contractStep!)}
              />
              <BaseRadio
                id="contractType3"
                name="contractType"
                label="모두싸인"
                checked={contractSignType.isModuSign}
                onChange={() => {
                  handleCheckEsignAvailable("MODU_SIGN");
                }}
                disabled={disabledEsignEdit.includes(contract?.contractStep!)}
              />
            </div>
          }
        />
      </div>
      {/* {isEsign ? ( */}
      {/* 직접 계약 */}
      {contractSignType.directContract && (
        <>
          <div className="flex-start">
            <BaseSection title={"계약서"} children={<ContractImageUpload isOnlyView type={"PROOF"} images={document} />} />
            <BaseSection title={"계약 필요 서류"} children={<ContractImageUpload isOnlyView type={"PROOF"} images={require} />} />
          </div>
          {detailData?.product?.productType === "TIME_COURT" && (
            <div className="flex-start">
              <section className="contents-container__grid">
                <div className="contents-container__grid-index"></div>
              </section>

              <BaseSection title={"결제 가능 수단"} children={<p>{getPgcodeLabel(contract?.pgcodeOption)}</p>} />
            </div>
          )}
        </>
      )}
      {/* 이싸인온 */}
      {contractSignType.isEsign && <EsignOnLink detailData={detailData} viewOnly={true} setData={setData} />}
      {/* 모두싸인 */}
      {contractSignType.isModuSign && <ModuSignContractSection viewOnly={true} />}
      <div className="flex-start">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection title={"관리비 청구 유형"} value={contract?.isMntContain ? "관리비 포함" : "관리비 별도"} />
        )}
      </div>

      {/* 연관 계약 (원계약 + 재계약) */}
      <RelatedContractsSection detailData={detailData} onClickRenewal={() => setIsRenewalOpen(true)} />
      {isRenewalOpen && <ContractRenewalModal isOpen={isRenewalOpen} detailData={detailData} onClose={() => setIsRenewalOpen(false)} />}

      {/* v.1.32.4  ps-1440 건으로 주석처리 (Harry)*/}

      {/* <div className="flex-start">
        {detailData?.product?.productType !== "TIME_COURT" && (
          <BaseSection
            title={"공문 수신자"}
            children={
              <>
                {contract?.billContactList?.map((list: any, index: number) => {
                  return (
                    <div className="flex-row flex-center" key={index}>
                      {list.receiveNotiType === "TALK_OFFICIALDOC" && (
                        <>
                          <div className="minmax435">
                            <BaseSection title={"이메일"} value={list?.email ? list?.email : "-"} />
                          </div>
                          <BaseSection title={"휴대폰"} value={list?.contact ? formatPhoneNumber(String(list?.contact)) : "-"} />
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            }
          />
        )}
      </div>
      {detailData?.product?.productType !== "TIME_COURT" && rentalSchedule?.isTaxInvoiceIssue && (
        <div className="flex-start">
          <BaseSection
            title={"세금계산서 수신자"}
            children={
              <>
                {contract?.billContactList?.map(
                  (list: any, index: number) =>
                    list.receiveNotiType !== "TALK_OFFICIALDOC" && (
                      <div className="flex-row flex-center-start" key={index}>
                        <BaseSection title={"이메일"} value={list?.email ? list?.email : "-"} />
                      </div>
                    ),
                )}
              </>
            }
          />
        </div>
      )} */}
    </>
  );
};
export default ContractInfoDetail;
